import { ChevronDown, ChevronUp, Square } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { create } from 'zustand';

import { cn } from '@/components/ui';
import { useMenuBar } from '@/services/MenuBarService';
import { NavItemType } from '@/types/menu';

import { NavItem } from './NavItem';

// The collapsed rail shows one flyout at a time, so the open one is shared between sections.
// Without this, moving from one section to the next briefly stacks two flyouts.
const useRailFlyout = create<{ openId: string | null; setOpenId: (id: string | null) => void }>(set => ({
  openId: null,
  setOpenId: openId => set({ openId }),
}));

interface Props {
  menu: NavItemType;
  level: number;
}

const NavCollapse = ({ menu, level }: Props) => {
  const drawerOpen = useMenuBar(state => state.drawerOpen);
  const { pathname } = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // After the rail collapses, ignore hover for a beat so Recipes (etc.) does not pop open under the cursor.
  const suppressFlyoutUntil = useRef(0);
  const flyoutId = `${level}:${menu.id ?? menu.title}`;
  const flyoutOpen = useRailFlyout(state => state.openId === flyoutId);
  const setFlyoutOpenId = useRailFlyout(state => state.setOpenId);
  // Expanded state of the section in the full sidebar; the rail flyout is tracked separately.
  const [expanded, setExpanded] = useState(false);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const open = drawerOpen ? expanded : flyoutOpen;

  const routeSelected = useMemo(
    () => menu.children?.some(item => item.url === pathname || !!(item.url && pathname.startsWith(item.url))) ?? false,
    [menu.children, pathname],
  );

  useEffect(() => {
    if (routeSelected) {
      setExpanded(true);
    }
  }, [routeSelected]);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  const closeFlyout = () => {
    cancelClose();
    if (useRailFlyout.getState().openId === flyoutId) {
      setFlyoutOpenId(null);
    }
  };

  // Close the flyout after navigating from it, when the rail expands/collapses, and on unmount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: carried over from the former ESLint suppression
  useEffect(() => {
    closeFlyout();
    if (!drawerOpen) {
      suppressFlyoutUntil.current = Date.now() + 300;
    }
  }, [drawerOpen, pathname]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: carried over from the former ESLint suppression
  useEffect(() => closeFlyout, []);

  // Keep the rail flyout inside the viewport so lower sections stay reachable.
  useLayoutEffect(() => {
    if (drawerOpen || !open || !flyoutRef.current) {
      return;
    }
    const margin = 8;
    const rect = flyoutRef.current.getBoundingClientRect();
    const maxTop = Math.max(margin, window.innerHeight - margin - rect.height);
    if (flyoutTop > maxTop) {
      setFlyoutTop(maxTop);
    }
  }, [drawerOpen, flyoutTop, open]);

  const openFlyout = (element: HTMLElement) => {
    if (drawerOpen || Date.now() < suppressFlyoutUntil.current) {
      return;
    }
    cancelClose();
    setFlyoutTop(element.getBoundingClientRect().top);
    setFlyoutOpenId(flyoutId);
  };

  const scheduleFlyoutClose = () => {
    if (drawerOpen) {
      return;
    }
    cancelClose();
    // A short grace period covers the gap between the rail and the flyout.
    closeTimer.current = setTimeout(closeFlyout, 150);
  };

  const navChildren = menu.children?.map(item => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <p key={item.id} className="px-3 py-2 text-center text-xs text-critical">
            Fix - Collapse or Item
          </p>
        );
    }
  });

  const Icon = menu.icon;
  const menuIcon = Icon ? (
    <Icon className="size-icon" />
  ) : level === 1 ? (
    <Square className="size-4" strokeWidth={1.75} />
  ) : null;
  // Collapsed rail hides the title text; keep an accessible name for the button.
  const menuLabel = typeof menu.title === 'string' ? menu.title : undefined;

  return (
    <div role="group" onMouseLeave={scheduleFlyoutClose} onMouseEnter={event => openFlyout(event.currentTarget)}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={!drawerOpen && level === 1 ? menuLabel : undefined}
        title={!drawerOpen && level === 1 ? menuLabel : undefined}
        className={cn(
          'group flex w-full items-center gap-2 min-h-9 rounded-auditor py-0.5 pr-2 text-nav font-medium transition-colors',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sidebar-ink/70',
          routeSelected && !(drawerOpen && open)
            ? 'bg-sidebar-active text-sidebar-ink shadow-auditor'
            : routeSelected
              ? 'text-sidebar-ink hover:bg-sidebar-ink/10'
              : 'text-sidebar-ink/80 hover:bg-sidebar-ink/10 hover:text-sidebar-ink',
          (drawerOpen || level !== 1)
            && {
              1: 'pl-1',
              2: 'pl-nav-1',
              3: 'pl-nav-2',
            }[level],
        )}
        onClick={event => {
          if (drawerOpen) {
            setExpanded(current => !current);
          } else {
            // Hover has usually opened it already, so a click must not toggle it shut.
            openFlyout(event.currentTarget);
          }
        }}
        onFocus={event => openFlyout(event.currentTarget)}
      >
        {menuIcon && (
          <span
            className={cn(
              'flex shrink-0 items-center justify-center',
              level === 1 && !drawerOpen ? 'w-mini-drawer' : 'size-8',
            )}
          >
            <span className="flex size-8 items-center justify-center">{menuIcon}</span>
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-left" aria-hidden={level === 1 && !drawerOpen ? true : undefined}>
          {menu.title}
        </span>
        {open ? (
          <ChevronUp className="size-4 shrink-0" aria-hidden={level === 1 && !drawerOpen ? true : undefined} />
        ) : (
          <ChevronDown className="size-4 shrink-0" aria-hidden={level === 1 && !drawerOpen ? true : undefined} />
        )}
      </button>

      {drawerOpen && open && <div className="mt-0.5 flex flex-col gap-0.5">{navChildren}</div>}

      {!drawerOpen && open && (
        <div
          ref={flyoutRef}
          role="group"
          className="scrollbar-sidebar fixed left-mini-drawer z-menu ml-1 flex max-h-dialog min-w-48 flex-col gap-0.5 overflow-y-auto rounded-auditor-lg bg-sidebar p-2 text-sidebar-ink shadow-auditor-overlay"
          style={{ top: flyoutTop }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleFlyoutClose}
        >
          <p className="m-0 px-2 py-1.5 text-caption font-semibold uppercase tracking-eyebrow text-sidebar-ink-muted">
            {menu.title}
          </p>
          {navChildren}
        </div>
      )}
    </div>
  );
};

export default NavCollapse;
